import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from './auth-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyOrganizations } from '../apiEndpoints';
import { useLocation } from 'react-router-dom';

interface OrganizationContextType {
    selectedOrganizerId: string | null;
    setSelectedOrganizerId: (id: string | null) => void;
    userOwnOrganizer: any;
    accessibleOrganizers: any;
    isLoading: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
    const context = useContext(OrganizationContext);
    if (context === undefined) {
        throw new Error('useOrganization must be used within an OrganizationProvider');
    }
    return context;
};

interface OrganizationProviderProps {
    children: React.ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
    const { user } = useAuth();
    const location = useLocation();
    const userId = user?.userId;
    const [selectedOrganizerId, setSelectedOrganizerId] = useState<string | null>(null);
    const queryClient = useQueryClient();
    const previousOrganizerIdRef = useRef<string | null>(null);

    // Fetch all user organizations (owned and admin) using the new endpoint
    const { data: myOrganizationsResponse, isLoading: isLoadingOrganizations } = useQuery<any>({
        queryKey: ['myOrganizations', userId],
        queryFn: async () => {
            if (!userId) throw new Error('No user ID');
            return await getMyOrganizations();
        },
        staleTime: 15 * 60 * 1000, // 15 minutes
        enabled: !!userId && location.pathname.includes('/organizations'),
    });

    // Extract user's own organizer (where userId matches) and accessible organizers from the response
    const { userOwnOrganizer, accessibleOrganizers } = useMemo(() => {
        const organizations = myOrganizationsResponse?.data || [];

        if (!organizations || organizations.length === 0) {
            return {
                userOwnOrganizer: null,
                accessibleOrganizers: null,
            };
        }

        // Find the organizer owned by the user (userId matches)
        // Handle both ObjectId and string formats
        const ownOrganizer = organizations.find((org: any) => {
            const orgUserId = org.userId?.toString() || org.userId;
            const currentUserId = userId?.toString() || userId;
            return orgUserId === currentUserId;
        });

        // All other organizations are accessible (admin role)
        const accessible = organizations.filter((org: any) => {
            const orgId = org._id?.toString() || org._id;
            const ownOrgId = ownOrganizer?._id?.toString() || ownOrganizer?._id;
            return orgId !== ownOrgId;
        });

        return {
            userOwnOrganizer: ownOrganizer || null,
            accessibleOrganizers: accessible.length > 0 ? { data: accessible } : null,
        };
    }, [myOrganizationsResponse, userId]);

    const isLoading = isLoadingOrganizations;

    // Load selected organization from localStorage on mount (before default selection)
    useEffect(() => {
        const savedOrganizerId = localStorage.getItem('selectedOrganizerId');
        if (savedOrganizerId) {
            setSelectedOrganizerId(savedOrganizerId);
        }
    }, []);

    // Set default selected organizer when data is loaded (first organization)
    // Only set default if no value is already set (from localStorage or previous selection)
    useEffect(() => {
        if (!selectedOrganizerId && !isLoading && myOrganizationsResponse?.data) {
            const organizations = myOrganizationsResponse.data;
            // Priority: User's own organizer first, then first accessible organizer
            if (userOwnOrganizer?._id) {
                setSelectedOrganizerId(userOwnOrganizer._id);
            } else if (organizations && organizations.length > 0) {
                setSelectedOrganizerId(organizations[0]._id);
            }
        }
    }, [userOwnOrganizer, myOrganizationsResponse, selectedOrganizerId, isLoading]);

    // Save organization to localStorage whenever it changes
    useEffect(() => {
        if (selectedOrganizerId) {
            localStorage.setItem('selectedOrganizerId', selectedOrganizerId);
        }
    }, [selectedOrganizerId]);

    // Refetch active queries when organization changes
    useEffect(() => {
        // Skip on initial mount (when previousOrganizerIdRef is null and selectedOrganizerId is set for the first time)
        if (previousOrganizerIdRef.current === null && selectedOrganizerId !== null) {
            previousOrganizerIdRef.current = selectedOrganizerId;
            return;
        }

        // Only invalidate if the organization actually changed
        if (previousOrganizerIdRef.current !== selectedOrganizerId && selectedOrganizerId !== null) {
            // Invalidate and refetch all active queries to show loading states and get fresh data for the new organization
            queryClient.invalidateQueries({ refetchType: 'active' });
            previousOrganizerIdRef.current = selectedOrganizerId;
        }
    }, [selectedOrganizerId, queryClient]);

    const value: OrganizationContextType = {
        selectedOrganizerId,
        setSelectedOrganizerId,
        userOwnOrganizer,
        accessibleOrganizers,
        isLoading,
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};

